﻿import React, { useEffect } from "react";
import { useState } from "react";
import { Typography, TextField, Grid, Box } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
} from "@/components/GenericComponents/ShepComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchItemQualifiers,
  checkIfHandlingEdi,
  selectEditCatalogData,
  selectIsEditMode,
  setCatalogDataField,
  selectAddEditConnectionInfo,
} from "@/features/connections/manageCatalogDataSlice";
import { defaultCode, blankCode } from "@/features/connections/integrationSlice";
import { AddEditCatalogPopup } from "./CatalogPopup";

const AddItemPopup = ({ onClose, onValidate, onComplete }) => {
  const dispatch = useDispatch();
  const dataValues = useSelector(selectEditCatalogData);
  const isEdit = useSelector(selectIsEditMode);
  const connectionInfo = useSelector(selectAddEditConnectionInfo);
  const [qualifierData, setQualifierData] = useState([]);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const qualifiers = useSelector(
    (state) => state.tempInfo.manageCatalogData.qualifiers,
  );
  const isEdi = useSelector(
    (state) => state.tempInfo.manageCatalogData.isEdiHandlingConnection,
  );

  useEffect(() => {
    if (connectionInfo.connectionId !== -1) {
      dispatch(
        checkIfHandlingEdi({ connectionId: connectionInfo.connectionId }),
      );
      dispatch(
        fetchItemQualifiers({ connectionId: connectionInfo.connectionId }),
      );
    }
  }, [connectionInfo, dispatch]);

  useEffect(() => {
    const combined = qualifierData.filter((q) => q.QualifierCode && q.Code);

    // Only include lookup qualifiers in the item code
    const lookupQualifiers = combined
      .filter((q) => q.IsLookup)
      .flatMap((q) => [q.QualifierCode, q.Code])
      .join("*");

    dispatch(
      setCatalogDataField({ name: "ItemCode", value: lookupQualifiers }),
    );
    //All qualifiers with codes are saved to the qualifiers dict in JSONProperties
    const allQualifiersDict = combined.reduce((acc, q) => {
      acc[q.QualifierCode] = q.Code;
      return acc;
    }, {});

    //setAllQualifiers(allQualifiersDict);
    if (allQualifiersDict != dataValues.ItemQualifiers) {
      dispatch(
        setCatalogDataField({
          name: "ItemQualifiers",
          value: allQualifiersDict,
        }),
      );
    }
  }, [qualifierData]);

  useEffect(() => {
    if (isEdi && qualifiers) {
      setQualifierData(
        qualifiers.map((q) => ({
          id: q.Qualifier,
          QualifierCode: q.Qualifier,
          Description: q.Description,
          Code:
            dataValues.ExistingQualifiers == null
              ? ""
              : dataValues.ExistingQualifiers[q.Qualifier],
          IsMandatory: Boolean(q.IsMandatory),
          IsLookup: Boolean(q.IsLookup),
        })),
      );
    }
  }, [qualifiers, dataValues.ExistingQualifiers]);

  const validateForm = () => {
    setSaveAttempted(true);
    if (onValidate) {
      const result = onValidate(dataValues);
      if (!result.isValid) return result;
    }
    if (dataValues.ItemCode === defaultCode) {
      return {
        isValid: false,
        message: `Item Code cannot be saved as ${defaultCode}`,
      };
    }
    if (dataValues.ItemCode === blankCode) {
      return {
        isValid: false,
        message: `Item Code cannot be saved as ${blankCode}`,
      };
    }
    if (dataValues.ItemCode?.includes(",")) {
      return {
        isValid: false,
        message: `The Item Code cannot include a ','`,
      };
    }
    if (
      qualifierData.find(
        (row) => row.IsMandatory && (!row.Code || row.Code.trim() === ""),
      )
    ) {
      return {
        isValid: false,
        message: "All mandatory qualifiers require a code.",
      };
    }
    if (isEdi && dataValues.ItemCode === "") {
      return {
        isValid: false,
        message: "Please select a lookup item qualifier.",
      };
    }
    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setQualifierData([]);
    //setAllQualifiers({});
    setSaveAttempted(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    dispatch(setCatalogDataField({ name, value }));
  };

  const handleQualifierCodeChange = (id, value) => {
    setQualifierData((prev) =>
      prev.map((q) => (q.id === id ? { ...q, Code: value } : q)),
    );
  };

  const columns = [
    {
      field: "QualifierCode",
      headerName: "Qualifier Code",
      width: 230,
    },
    {
      field: "Description",
      headerName: "Description",
      width: 350,
    },
    {
      field: "Code",
      headerName: "Code",
      width: 200,
      renderCell: (params) => (
        <TextField
          disabled={isEdit && params.row.IsLookup}
          variant="outlined"
          size="small"
          value={params.row.Code || ""}
          error={saveAttempted && params.row.IsMandatory && !params.row.Code}
          onChange={(e) =>
            handleQualifierCodeChange(params.row.id, e.target.value)
          }
        />
      ),
    },
    {
      field: "IsLookup",
      headerName: "Lookup",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <ShepCheckbox
            checked={Boolean(params.row.IsLookup)}
            onChange={() => {}}
            readOnly
          />
        </Box>
      ),
    },
    {
      field: "IsMandatory",
      headerName: "Mandatory",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <ShepCheckbox
            checked={Boolean(params.row.IsMandatory)}
            onChange={() => {}}
            readOnly
          />
        </Box>
      ),
    },
  ];

  return (
    <AddEditCatalogPopup
      onClose={handleClose}
      onValidate={validateForm}
      onComplete={onComplete}
    >
      <Grid item xs={4}>
        <Typography>Item Code </Typography>
        <Typography>
          <TextField
            disabled={isEdi || isEdit}
            variant="outlined"
            name="ItemCode"
            value={dataValues.ItemCode}
            onChange={(e) =>
              dispatch(
                setCatalogDataField({
                  name: "ItemCode",
                  value: e.target.value,
                }),
              )
            }
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
              ...(isEdi && {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#EBEBEB",
                },
              }),
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Item Description</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="ItemDesc"
            value={dataValues?.ItemDesc || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Weight</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="Weight"
            type="text"
            value={dataValues?.Weight ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                handleChange({
                  target: { name: "Weight", value: null },
                });
              } else if (/^\d+\.?\d*$/.test(value)) {
                handleChange({
                  target: { name: "Weight", value: parseFloat(value) },
                });
              }
            }}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      {isEdi && (
        <>
          <Grid item xs={4} />
          <Grid item xs={4} />
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              paddingLeft: 2,
            }}
          >
            <ShepDataGridPro
              isDataLoaded={true}
              rows={qualifierData || []}
              rowCount={qualifierData ? qualifierData.length : 0}
              columns={columns}
              columnHeaderHeight={78}
              rowHeight={100}
              whiteHeader={true}
              checkboxSelection={false}
              disableMultipleRowSelection={false}
              disableColumnMenu={true}
              disableColumnFilter={true}
              disableColumnReorder={true}
              hideFooter={true}
              sx={{
                color: "var(--text-primary)",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid:hover": {
                  backgroundColor: "inherit",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                  {
                    outline: "none",
                  },
                "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                  {
                    opacity: 1,
                  },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                  {
                    borderBottomLeftRadius: "4px",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "var(--primary-pale)",
                    borderBottom: "none",
                    borderLeft: "none",
                  },
                "& .MuiDataGrid-columnHeader:first-of-type": {
                  paddingLeft: "20px",
                },
                "& .MuiDataGrid-cell:first-of-type": {
                  paddingLeft: "20px",
                },
              }}
            />
          </Box>
        </>
      )}
    </AddEditCatalogPopup>
  );
};

export default AddItemPopup;
